import React, { ReactElement, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDebounce } from "react-use";
import dayjs from "dayjs";
import { formatCouponStatus } from "helpers/configurator";

import SearchIcon from "@mui/icons-material/Search";

import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import { getAllCoupons } from "redux/handlers/couponHandler";

import { useAppDispatch } from "hooks/reduxHook";

import { ICouponData } from "types/api/couponApiInterface";

import AddCoupon from "./actions/AddCoupon";
import CouponMenu from "./CouponMenu";

const Coupon = (): ReactElement => {
 const dispatch = useAppDispatch();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("lg"));

 const searchRef = useRef<HTMLInputElement>(null);

 const [loading, setLoading] = useState<boolean>(true);
 const [currentPage, setCurrentPage] = useState<number>(0);
 const [rowPerPage, setRowPerPage] = useState<number>(10);
 const [searchValue, setSearchValue] = useState<string>("");
 const [couponList, setCouponList] = useState<Array<ICouponData>>([]);
 const [couponNumber, setCouponNumber] = useState<number>(0);

 useEffect(() => {
  (async () => {
   setLoading(true);
   const couponData = await dispatch(getAllCoupons(0, 10));
   if (couponData) {
    setCouponList(couponData.dataset);
    setCouponNumber(couponData.totalCount);
   }
   setLoading(false);
  })();
 }, []);

 useDebounce(
  async () => {
   if (!loading) {
    setCurrentPage(0);
    const couponData = await dispatch(
     getAllCoupons(currentPage, rowPerPage, searchRef?.current?.value)
    );
    if (couponData) {
     setCouponList(couponData.dataset);
     setCouponNumber(couponData.totalCount);
    }
   }
  },
  1000,
  [searchValue]
 );

 const handleOnRowsPerPageChange = async (
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
 ) => {
  const newRowsPerPage = parseInt(event.target.value, 10);
  setRowPerPage(newRowsPerPage);
  setCurrentPage(0);
  const couponData = await dispatch(getAllCoupons(0, newRowsPerPage, searchRef?.current?.value));
  if (couponData) {
   setCouponList(couponData.dataset);
   setCouponNumber(couponData.totalCount);
  }
 };

 const handleOnPageChange = async (
  event: React.MouseEvent<HTMLButtonElement> | null,
  newPage: number
 ) => {
  const startIndex = newPage * rowPerPage;
  setCurrentPage(newPage);
  const couponData = await dispatch(
   getAllCoupons(startIndex, rowPerPage, searchRef?.current?.value)
  );
  if (couponData) {
   setCouponList(couponData.dataset);
   setCouponNumber(couponData.totalCount);
  }
 };

 const handleReloadData = async () => {
  const couponData = await dispatch(
   getAllCoupons(currentPage, rowPerPage, searchRef?.current?.value)
  );
  if (couponData) {
   setCouponList(couponData.dataset);
   setCouponNumber(couponData.totalCount);
  }
 };

 const RenderCouponStatus = ({
  active,
  expire,
  couponActive
 }: {
  active: string | null;
  expire: string | null;
  couponActive: boolean;
 }) => {
  if (couponActive) {
   if (active && expire) {
    const status = formatCouponStatus(active, expire);
    if (status === "expired") {
     return <Chip label="active - expired" size="small" color="error" />;
    } else if (status === "expiring") {
     return <Chip label="active - expiring" size="small" color="warning" />;
    } else {
     return <Chip label="active" size="small" color="success" />;
    }
   } else {
    return <Chip label="enabled - waiting date..." size="small" color="info" />;
   }
  } else {
   if (active && expire) {
    const status = formatCouponStatus(active, expire);
    if (status === "expired") {
     return <Chip label="disabled - expired" size="small" color="error" />;
    } else if (status === "expiring") {
     return <Chip label="disabled - expiring" size="small" color="warning" />;
    } else {
     return <Chip label="disabled" size="small" color="error" />;
    }
   } else if (!active && !expire) {
    return <Chip label="disabled - waiting date..." size="small" color="error" />;
   } else {
    return <Chip label="disabled" size="small" color="error" />;
   }
  }
 };

 return !loading ? (
  <Stack spacing={2}>
   <Stack justifyContent="space-between" direction="row">
    <Stack direction={desktopViewPort ? "row" : "column"} spacing={2} alignItems="center">
     <Typography>
      {couponNumber} <FormattedMessage id="configurator.coupon.totalCoupons" />
     </Typography>
     <TextField
      size="small"
      autoComplete="new-password"
      label={<FormattedMessage id="configurator.search.coupon" />}
      inputRef={searchRef}
      sx={{ ml: 4 }}
      InputLabelProps={{ shrink: true }}
      InputProps={{ endAdornment: <SearchIcon color="disabled" />, autoComplete: "off" }}
      onChange={({ currentTarget }) => setSearchValue(currentTarget.value)}
     />
    </Stack>
    <Stack justifyContent={desktopViewPort ? "flex-start" : "center"}>
     <AddCoupon reloadData={handleReloadData} />
    </Stack>
   </Stack>
   <Stack>
    <TableContainer component="div">
     <Table sx={{ minWidth: 650 }} aria-label="simple table">
      <TableHead>
       <TableRow>
        <TableCell>
         <FormattedMessage id="configurator.coupon.status" />
        </TableCell>
        <TableCell>
         <FormattedMessage id="configurator.coupon.code" />
        </TableCell>
        <TableCell>
         <FormattedMessage id="configurator.coupon.sale" />
        </TableCell>
        <TableCell>
         <FormattedMessage id="configurator.coupon.redeem" />
        </TableCell>
        <TableCell>
         <FormattedMessage id="configurator.coupon.valid" />
        </TableCell>
        <TableCell align="center">
         <FormattedMessage id="configurator.coupon.actions" />
        </TableCell>
       </TableRow>
      </TableHead>
      <TableBody>
       {couponList.map((row, index) => (
        <TableRow key={`isp-config-list-${index}`}>
         <TableCell component="th" scope="row">
          <RenderCouponStatus
           active={row.activation_date}
           expire={row.expire}
           couponActive={row.active}
          />
         </TableCell>
         <TableCell component="th" scope="row">
          <Typography variant="subtitle2">{row.code}</Typography>
         </TableCell>
         <TableCell component="th" scope="row">
          <Typography variant="subtitle2">{`${row.percentage}%`}</Typography>
         </TableCell>
         <TableCell>
          {row.redeem_max === -1 ? (
           <Typography variant="caption">
            <FormattedMessage id="app.unlimited" />
           </Typography>
          ) : (
           <Stack>
            <Stack direction="row" alignItems="center">
             <Typography variant="caption" minWidth={120}>
              <FormattedMessage id="configurator.coupon.redeemMax" />
             </Typography>
             <Typography variant="caption">{row.redeem_max}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
             <Typography variant="caption" minWidth={120}>
              <FormattedMessage id="configurator.coupon.redeemed" />
             </Typography>
             <Typography variant="caption">{row.times_redeemed}</Typography>
            </Stack>
           </Stack>
          )}
         </TableCell>
         <TableCell>
          {row.activation_date && row.expire ? (
           <Stack>
            <Stack direction="row" alignItems="center">
             <Typography variant="caption" minWidth={120}>
              <FormattedMessage id="configurator.coupon.activationDate" />
             </Typography>
             <Typography variant="caption">
              {dayjs(row.activation_date).format("DD/MM/YYYY")}
             </Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
             <Typography variant="caption" minWidth={120}>
              <FormattedMessage id="configurator.coupon.expireDate" />
             </Typography>
             <Typography variant="caption">{dayjs(row.expire).format("DD/MM/YYYY")}</Typography>
            </Stack>
           </Stack>
          ) : (
           <Typography variant="caption">
            <FormattedMessage id="configurator.coupon.notActive" />
           </Typography>
          )}
         </TableCell>
         <TableCell align="center">
          <CouponMenu coupon={row} reloadData={handleReloadData} />
         </TableCell>
        </TableRow>
       ))}
      </TableBody>
     </Table>
    </TableContainer>
    <TablePagination
     component="div"
     count={couponNumber}
     page={currentPage}
     rowsPerPage={rowPerPage}
     onPageChange={handleOnPageChange}
     onRowsPerPageChange={handleOnRowsPerPageChange}
    />
   </Stack>
  </Stack>
 ) : (
  <Stack>
   <CircularProgress />
  </Stack>
 );
};

export default Coupon;
